<template>
    <tr>
        <td class="pl-0 px-1">
            <div v-if="isEditing" class="drag-icon">
                <v-img :max-width="10" :src="require('../../assets/drag-icon.svg')"/>
            </div>
            <div class="regular-disabled">
                <ComboboxDropdown
                    :items="products"
                    :selected="products && row && row.product_id ? products.filter(x => x.id === row.product_id) : []"
                    name-key="name"
                    value-key="id"
                    show-selected-first
                    :disabled="!userCanEdit"
                    :placeholder="$t('Product / description')"
                    :outlined="false"
                    :readonly="false"
                    dense-top
                    :searchable="false"
                    :search-value="selectedProduct"
                    :hide-details="!errorsExist"
                    :error-messages="row.description_error"
                    :deselect="false"
                    :input-text="selectedProduct ? selectedProduct : ''"
                    @input="product_select"
                    @textInput="product_select"
                >
                    <template v-if="can_edit_account(accountId)" v-slot:actions>
                        <v-btn
                            text
                            large
                            color="primary"
                            @click.stop="add_new_product"
                        >
                            <v-icon size="24" left class="ml-1">
                                add_box
                            </v-icon>
                            {{ $t("New") + " " + $t("product") }}
                        </v-btn>
                    </template>
                </ComboboxDropdown>
            </div>
        </td>
        <td class="px-1">
            <v-text-field
                v-model="row.quantity"
                class="text-right regular-disabled input-align-right pt-0 mt-0"
                label=""
                single-line
                :hide-details="!errorsExist"
                :error-messages="row.quantity_error"
                :disabled="!userCanEdit"
                @change="quantity_changed"
                @keypress="filterNumber"
            />
        </td>
        <td class="px-1 regular-disabled">
            <ComboboxDropdown
                :items="units"
                :selected="units ? units.filter(x => x.value === row.unit) : []"
                name-key="text"
                value-key="value"
                show-selected-first
                :input-text="row.unit ? row.unit : ''"
                @input="row.unit = $event.value"
                @textInput="row.unit = $event"
                :disabled="!userCanEdit"
                :outlined="false"
                :readonly="false"
                :searchable="false"
                dense-top
                :deselect="false"
                :hide-details="!errorsExist"
            />
        </td>
        <td class="px-1">
            <v-text-field
                :value="formatMoneyNumber(row.net_price)"
                label=""
                single-line
                class="regular-disabled input-align-right pt-0 mt-0"
                :hide-details="!errorsExist"
                :error-messages="row.net_price_error"
                :disabled="!userCanEdit"
                @change="price_changed"
                @keypress="filterNumber"
            />
        </td>
        <td class="px-1">
            <v-text-field
                :value="formatNumber(calculatedDiscountRate)"
                label=""
                single-line
                class="regular-disabled input-align-right pt-0 mt-0"
                suffix="%"
                :hide-details="!errorsExist"
                :error-messages="row.discount_rate_error"
                :disabled="!userCanEdit"
                @change="discountChanged"
                @keypress="filterNumber"
            />
        </td>

        <td class="px-1 regular-disabled">
            <ComboboxDropdown
                :items="taxes ? taxes : []"
                :selected="taxes ? taxes.filter(x => x.id === row.tax_id) : []"
                name-key="info"
                value-key="id"
                :input-text="showTaxValue(taxes, row)"
                :sort="false"
                show-selected-first
                deselect
                label=""
                :hide-details="!errorsExist"
                :error-messages="taxError(row.id)"
                dense-top
                :disabled="!userCanEdit"
                :outlined="false"
                textClass="text-right regular-disabled input-align-right pt-0 mt-0"
                @input="setTax"
            />
        </td>

        <td class="px-1">
            <v-text-field
                class="text-right regular-disabled input-align-right pt-0 mt-0"
                label=""
                single-line
                :hide-details="!errorsExist"
                :value="formatMoneyNumber(sum)"
                :disabled="true"
            />
        </td>
        <td
            :hidden="!isEditing"
            class="pl-1 pr-0 actions pt-1 d-flex justify-between">

            <v-text-field
                v-if="!isEditing & canConfirmInvoiceRows & !!completedAt"
                class="text-left regular-disabled input-align-left pt-0 mt-0"
                label=""
                single-line
                :value="completedAt"
                :hide-details="true"
                :disabled="true"
            />
            <v-checkbox
                v-if="!isEditing & canConfirmInvoiceRows & showConfirmCheckbox"
                v-model="invoiceRowConfirmed"
                hide-details
                class="mt-0 pl-3"
                style="max-width: 40px !important;"
            ></v-checkbox>

            <SalesInvoiceEditRowOptions
                v-if="userCanEdit"
                :item="row"
                :account-id="accountId"
                is-invoice
            />
            <action-menu
                v-if="userCanEdit"
                :items="actionItems"
                @action="actionItems[$event].action()"
            />
        </td>
    </tr>
</template>

<script>
import {$t} from "@/i18n";
import Vue from "vue";
import AppModule from "../../store/modules/app";
import {computed, onMounted, ref} from "@vue/composition-api";
import {
    parseSysDate,
    formatNumber,
    formatDate,
    formatMoneyNumber,
    cleanNumber,
} from "@/plugins/formatting";
import ComboboxDropdown from "@/components/ComboboxDropdown";
import ProductModule from "@/store/modules/product";
import {filterNumber} from "../../helpers/helpers";
import SalesInvoiceEditRowOptions from "./SalesInvoiceEditRowOptions.vue";
import DialogModule from "../../store/modules/dialog";
import AccountModule from "../../store/modules/account";
import SalesInvoiceModule from "@/store/modules/sales_invoice";
import RightsModule from "../../composables/use-rights";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import ProductFieldModule from "../../store/modules/product_field";

export default {
    name: "SalesInvoiceEditRow",
    components: {
        ComboboxDropdown,
        SalesInvoiceEditRowOptions
    },
    props: {
        row: {type: Object, required: true},
        index: {type: Number, required: true},
        localeCode: {type: String, required: true},
        taxes: {type: Array, required: false, default: Array},

        isOpen: {type: Boolean, default: false},
        isEditing: {type: Boolean, default: false},

        currency: {type: String, required: true},
        accountId: {type: Number, required: true},
        canConfirmInvoiceRows: {type: Boolean, required: true},
        productFieldResources: {type: Array, required: false, default: Array},
        invoiceId: {type: Number, default: null},
        canEditAccount: {type: Boolean, required: true},
    },

    setup(props, ctx) {
        const {current_account, getAccount} = AccountModule();
        const {updateCompletedAt} = SalesInvoiceModule();
        const {openDialog, setDialogOptions} = DialogModule();
        const {can_edit_account} = RightsModule();

        const setTax = (tax_data) => {
            props.row.tax_id = tax_data.id
            props.row.tax_rate = tax_data.tax_rate
            props.row.tax_name = tax_data.name | ''
        };

        const productFieldModule = ProductFieldModule();
        const productFieldUnits = computed(() => {
            return productFieldModule.get_account_units(props.accountId).value.map(x => ({
                value: x.code,
                text: x.label
            }))
        })

        const taxWarning = []
        const taxError = (row_id) => {
            /* for testing purposes you can show different tax rates as "errors"
            if (row_id && taxWarning.includes(row_id)) {
                return [row_id]
            }
             */
        }

        const showTaxValue = (taxes, row) => {
            let result = ''
            const tax = taxes && row && row.tax_id ? taxes.find(x => x.id === row.tax_id) : null
            let tax_rate = tax && tax.rate ? tax.rate : 0
            let tax_percent = tax && tax.percent ? tax.percent : ''

            let row_rate = row && row.tax_rate !== null ? parseFloat(row.tax_rate) : 0
            let row_percent = row && row.tax_rate !== null ? '' + (Math.round(1000000 * row.tax_rate) / 10000) : ''
            if (row && props.isEditing && tax_percent !== row_percent) {
                // console.info('row_id=' + row.id + ', tax_rate=' + tax_percent + ', row_rate=' + row_percent)
                taxWarning.push(row.id)
            }

            let is_recalculate = props.isOpen || props.isEditing
            if (is_recalculate) {
                // if invoice is "OPEN" then recalculate taxes values
                if (tax) {
                    if (tax.percent) {
                        result = tax.percent + ' %';
                    } else result = '-';
                }
            } else {
                // if invoice is "CLOSED" then show saved values
                if (row && row.tax_rate !== null) {
                    result = (Math.round(1000000 * row.tax_rate) / 10000) + ' %'
                } else result = '-'
            }
            return result
        }

        const units = computed(() => {
            return [
                {value: "month", text: $t("Month")},
                {value: "piece", text: $t("Piece")},
                {value: "hour", text: $t("Hour")},
                ...productFieldUnits.value
            ]
        })

        const calculatedDiscountRate = computed({
            get: () =>
                isNaN(props.row.discount_rate) ? 0 : Math.round(props.row.discount_rate * 10000) / 100,
            set: value => {
                props.row.discount_rate = value / 100;
            }
        });
        const completedAt = computed(() => {
            return props.row.completed_at ? formatDate(props.row.completed_at) : false
        });

        const calculatedPrice = computed(() =>
            isNaN(props.row.net_price) ? 0 : props.row.net_price
        );
        const calculatedQuantity = computed(() =>
            isNaN(props.row.quantity) ? 0 : props.row.quantity
        );

        const sum = computed(() =>
            calculatedQuantity.value *
            calculatedPrice.value *
            (1 - props.row.discount_rate)
        );
        const menu = ref(false);
        const {products} = ProductModule();
        const invoiceRowConfirmed = computed({
            get: () => Boolean(props.row.completed_at),
            set: e => onClickCompletedAt(e),
        });

        const created = () => {
            Vue.set(props.row, "description_error", null);
            Vue.set(props.row, "tax_error", null);
            Vue.set(props.row, "net_price_error", null);
            Vue.set(props.row, "quantity_error", null);
            Vue.set(props.row, "discount_rate_error", null);
        };
        created();

        const price_changed = data => {
            let value = Number(cleanNumber(data));

            if (isNaN(value)) {
                return (props.row.net_price = 0);
            } else {
                value = Math.round(parseFloat(value) * 100);
                return (props.row.net_price = value / 100);
            }
        };

        const quantity_changed = data => {
            let value = Number(cleanNumber(data));

            if (isNaN(value)) {
                return props.row.quantity = 0;
            } else {
                value = Math.round(parseFloat(value) * 100);
                return (props.row.quantity = value / 100);
            }
        };
        const discountChanged = data => {
            data = data.replace("%", "");
            calculatedDiscountRate.value = Number(cleanNumber(data));
            if (calculatedDiscountRate.value > 100)
                calculatedDiscountRate.value = 0;
        };

        const delete_row = () => {
            return ctx.emit("delete_row", props.index);
        };
        const product_select = data => {
            if (typeof data === "object") {

                try {
                    // use active period first net price
                    let net_price;
                    let quantity;
                    if (data.product_prices && data.product_prices.length) {
                        let now = new Date();
                        for (let i = 0; i < data.product_prices.length; i++) {
                            let pp = data.product_prices[i];
                            let active_from = active_from ? parseSysDate(pp.active_from) : false;
                            if (!active_from || now >= active_from) {
                                net_price = pp.net_price;
                                quantity = pp.quantity;
                                break;
                            }
                        }
                    }
                    if (net_price) props.row.net_price = net_price;
                    if (quantity) props.row.quantity = quantity;
                } catch (e) {
                    errorDialog.showError(e)
                }

                props.row.product_id = data.id;
                props.row.description = data.name;
                if (
                    data.translations &&
                    data.translations.names &&
                    data.translations.names[props.localeCode]
                ) {
                    props.row.description =
                        data.translations.names[props.localeCode];
                }

                props.row.article_id = data.article_id;
                props.row.resource_id = data.resource_id;
                props.row.billing_project_id = data.billing_project_id;
                props.row.cost_center_id = data.cost_center_id;

                props.row.ext_variable = data.ext_variable;

                if (data._data && data._data.ext_unit_name) {
                    props.row.unit = data._data.ext_unit_name
                } else {
                    props.row.unit = data.unit;
                }

                props.row.tax_id = data.tax_id;
                props.row.tax_name = data.tax_name;
                props.row.tax_rate = data.tax_rate;

                if (data.discount_rate) {
                    props.row.discount_rate = data.discount_rate;
                }

                ctx.emit("update-row");

            } else {
                props.row.description = data;
            }
        };

        const add_new_product = () => {
            return ctx.emit("add_new_product", {index: props.index, name});
        };

        const selectedProduct = computed(() => props.row.description);

        const showConfirmCheckbox = computed(() => {
            if (props.isEditing) return false
            if (props.productFieldResources.length === 0) return false

            const productFieldResource = props.productFieldResources.find(x => x.code === props.row.resource_id)
            if (productFieldResource && productFieldResource._data && productFieldResource._data.person_ids) {
                return productFieldResource._data.person_ids.includes(current_account.value.id)
            }
        });

        const onClickCompletedAt = is_completed => {
            updateCompletedAt(props.row.id, props.invoiceId, is_completed)
                .then(resp => {
                    if (resp.completed_at) {
                        props.row.completed_at = resp.completed_at;
                    } else {
                        props.row.completed_at = null;
                    }
                })
                .catch(ex => errorDialog.showError(ex));
        };

        const actionItems = ref([{
            text: $t("Delete row"),
            action: () => delete_row()
        }]);

        const errorsExist = computed(() => {
            const errorKeys = Object.keys(props.row).filter(x =>
                x.includes("_error")
            );
            return Boolean(errorKeys.find(x => props.row[x]));
        });

        const userCanEdit = computed(() => {
            return (
                props.isEditing
                && (props.canEditAccount
                    || (current_account.value.id === props.row.person_id)
                    || props.row.is_new_row)
            );
        });

        return {
            sum,
            delete_row,
            quantity_changed,
            price_changed,
            products,
            menu,
            product_select,
            add_new_product,
            discountChanged,
            filterNumber,
            selectedProduct,
            calculatedDiscountRate,
            showConfirmCheckbox,
            onClickCompletedAt,
            invoiceRowConfirmed,
            current_account,
            actionItems,
            errorsExist,
            userCanEdit,
            completedAt,
            can_edit_account,
            units,
            formatNumber,
            formatMoneyNumber,
            setTax,
            showTaxValue,
            taxError,
        };
    }
};
</script>